import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, CreditCard, Loader2 } from "lucide-react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import api, { resolveMedia } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useSettings } from "@/context/SettingsContext";
import { useStudio, getStudioKey } from "@/lib/studio";
import useSEO from "@/lib/useSEO";

const eur = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(n || 0);

function F({ label, children, testId, required }) {
  return (
    <div data-testid={testId}>
      <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">{label}{required && <span className="text-ink"> *</span>}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

const emptyAddr = { name: "", street: "", number: "", zip: "", city: "", province: "", country: "Italia", extra: "", notes: "" };

export default function Checkout() {
  useSEO({ title: "Checkout — Shop Giovanna Matino Interior", description: "Completa il tuo ordine in sicurezza: spedizione, fatturazione e pagamento online." });
  const cart = useCart();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [customer, setCustomer] = useState({ name: "", surname: "", email: "", phone: "" });
  const [shipping, setShipping] = useState({ ...emptyAddr });
  const [billingSame, setBillingSame] = useState(true);
  const [billing, setBilling] = useState({ ...emptyAddr });
  const [billingType, setBillingType] = useState("privato");
  const [fiscal, setFiscal] = useState({ fiscal_code: "", vat_number: "", sdi_code: "", pec: "" });
  const [coupon, setCoupon] = useState("");
  const [accept, setAccept] = useState({ terms: false, privacy: false, returns: false, marketing: false });
  const [method, setMethod] = useState("stripe");
  const [gcCode, setGcCode] = useState("");
  const [gc, setGc] = useState(null); // Gift Card validata dal server: {balance}
  const [gcBusy, setGcBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [paypalCfg, setPaypalCfg] = useState(null);
  const [orderCtx, setOrderCtx] = useState(null); // {order_id, order_number} per i bottoni PayPal
  // Chiave idempotenza checkout: stabile finché il carrello non cambia.
  // Doppio click / retry / rientro dalla pagina Stripe non creano ordini o sessioni duplicate.
  const [idemKey, setIdemKey] = useState(() => crypto.randomUUID());
  const cartFingerprint = JSON.stringify(cart.items.map((i) => [i.product_id, i.variant, i.quantity]));
  useEffect(() => { setIdemKey(crypto.randomUUID()); }, [cartFingerprint]);
  const { studio, priceFor, active: studioActive, percent: studioPercent } = useStudio();

  const studioSavings = useMemo(() => {
    if (!studioActive) return 0;
    return cart.items.reduce((s, i) => {
      const reserved = priceFor({ id: i.product_id }, i.price);
      return reserved !== null && reserved < i.price ? s + (i.price - reserved) * i.quantity : s;
    }, 0);
  }, [studioActive, cart.items, priceFor]);

  // Gift Card: saldo verificato dal server; l'importo applicato è ricalcolato anche lì
  const gcApplied = gc ? Math.min(Number(gc.balance), Math.max(0, cart.total - studioSavings + shippingCost)) : 0;
  const finalTotal = Math.max(0, cart.total - studioSavings + shippingCost - gcApplied);

  const validateGc = async () => {
    if (!gcCode.trim()) return;
    setGcBusy(true);
    try {
      const { data } = await api.post("/gift-cards/validate", { code: gcCode.trim() });
      if (data.valid) {
        setGc(data);
        toast.success(`Gift Card applicata: credito disponibile ${eur(data.balance)}`);
      } else {
        setGc(null);
        toast.error(data.error || "Codice non valido");
      }
    } catch (err) {
      setGc(null);
      toast.error(err.response?.data?.detail || "Verifica non riuscita");
    } finally { setGcBusy(false); }
  };

  useEffect(() => {
    api.get("/payments/paypal/config").then((r) => setPaypalCfg(r.data)).catch(() => setPaypalCfg({ enabled: false }));
  }, []);
  useEffect(() => {
    if (params.get("annullato")) toast.info("Pagamento annullato. Il tuo carrello è ancora qui.");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const shippingCost = useMemo(() => {
    const cost = Number(settings.shop_shipping_cost) || 0;
    const freeOver = Number(settings.shop_free_shipping_over) || 0;
    return freeOver > 0 && cart.total >= freeOver ? 0 : cost;
  }, [settings, cart.total]);

  const leadTimes = useMemo(() => Array.from(new Set(cart.items.map((i) => i.lead_time).filter(Boolean))), [cart.items]);

  const buildPayload = () => ({
    items: cart.items.map(({ product_id, variant, quantity }) => ({ product_id, variant: variant || "", quantity })),
    customer, shipping,
    billing_same: billingSame,
    billing: billingSame ? shipping : { ...billing, ...fiscal },
    billing_type: billingType,
    coupon,
    method,
    accept_terms: accept.terms, accept_privacy: accept.privacy, accept_returns: accept.returns,
    marketing_consent: accept.marketing,
    studio_key: getStudioKey(),
    idempotency_key: idemKey,
    giftcard_code: gc ? gcCode.trim().toUpperCase() : "",
    website: "",
  });

  const valid = cart.items.length > 0
    && customer.name.trim() && customer.surname.trim() && /\S+@\S+\.\S+/.test(customer.email) && customer.phone.trim()
    && shipping.name.trim() && shipping.street.trim() && shipping.zip.trim() && shipping.city.trim() && shipping.province.trim()
    && (billingSame || (billing.name.trim() && billing.street.trim() && billing.zip.trim() && billing.city.trim() && billing.province.trim()))
    && (billingType === "privato" || fiscal.vat_number.trim())
    && accept.terms && accept.privacy && accept.returns;

  const payStripe = async () => {
    if (!valid) { toast.error("Completa tutti i campi obbligatori e accetta le condizioni"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/shop/checkout", buildPayload());
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nella creazione del pagamento");
      setBusy(false);
    }
  };

  const startPaypal = async () => {
    if (!valid) { toast.error("Completa tutti i campi obbligatori e accetta le condizioni"); throw new Error("invalid"); }
    const { data } = await api.post("/shop/checkout", buildPayload());
    setOrderCtx({ order_id: data.order_id, order_number: data.order_number });
    return data.order_id;
  };

  if (settings.shop_visible === false) {
    return (
      <div className="container-x py-24 text-center" data-testid="checkout-off">
        <h1 className="font-serif text-4xl">Lo Shop non è al momento disponibile online.</h1>
        <p className="text-taupe mt-4 text-sm">Per informazioni sui prodotti contatta direttamente lo Studio.</p>
        <Link to="/contatti" className="btn-ink mt-8 inline-flex">Contattaci</Link>
      </div>
    );
  }

  if (cart.items.length === 0) {
    return (
      <div className="container-x py-24 text-center" data-testid="checkout-empty">
        <h1 className="font-serif text-4xl">Il carrello è vuoto.</h1>
        <Link to="/shop" className="btn-ink mt-8 inline-flex">Vai allo Shop</Link>
      </div>
    );
  }

  return (
    <div className="container-x py-10 md:py-16 max-w-6xl" data-testid="page-checkout">
      <Link to="/shop" className="text-xs tracking-[0.14em] uppercase text-taupe hover:text-ink inline-flex items-center gap-2 min-h-[44px]" data-testid="checkout-back"><ArrowLeft size={14} /> Torna allo Shop</Link>
      <h1 className="font-serif text-4xl md:text-5xl mt-4">Checkout</h1>
      <p className="text-taupe mt-3 text-sm">Nessuna registrazione richiesta. Pagamento sicuro con Stripe o PayPal.</p>

      <div className="grid lg:grid-cols-12 gap-10 mt-10 items-start">
        <div className="lg:col-span-7 space-y-8">
          {/* Cliente */}
          <section className="bg-white border border-hair p-6 md:p-8" data-testid="checkout-customer">
            <div className="eyebrow mb-5">1 · Dati del cliente</div>
            <div className="grid sm:grid-cols-2 gap-4">
              <F label="Nome" required testId="f-name"><Input value={customer.name} onChange={(e) => setCustomer({ ...customer, name: e.target.value })} className="rounded-none" data-testid="checkout-name" /></F>
              <F label="Cognome" required testId="f-surname"><Input value={customer.surname} onChange={(e) => setCustomer({ ...customer, surname: e.target.value })} className="rounded-none" data-testid="checkout-surname" /></F>
              <F label="Email" required testId="f-email"><Input type="email" value={customer.email} onChange={(e) => setCustomer({ ...customer, email: e.target.value })} className="rounded-none" data-testid="checkout-email" /></F>
              <F label="Telefono" required testId="f-phone"><Input value={customer.phone} onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} className="rounded-none" data-testid="checkout-phone" /></F>
            </div>
          </section>

          {/* Spedizione */}
          <section className="bg-white border border-hair p-6 md:p-8" data-testid="checkout-shipping">
            <div className="eyebrow mb-5">2 · Indirizzo di spedizione</div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2"><F label="Nome e cognome del destinatario" required testId="f-shipname"><Input value={shipping.name} onChange={(e) => setShipping({ ...shipping, name: e.target.value })} className="rounded-none" data-testid="checkout-ship-name" /></F></div>
              <F label="Indirizzo" required testId="f-street"><Input value={shipping.street} onChange={(e) => setShipping({ ...shipping, street: e.target.value })} className="rounded-none" data-testid="checkout-ship-street" /></F>
              <F label="Numero civico" testId="f-number"><Input value={shipping.number} onChange={(e) => setShipping({ ...shipping, number: e.target.value })} className="rounded-none" data-testid="checkout-ship-number" /></F>
              <F label="CAP" required testId="f-zip"><Input inputMode="numeric" maxLength={5} value={shipping.zip} onChange={(e) => setShipping({ ...shipping, zip: e.target.value.replace(/\D/g, "") })} className="rounded-none" data-testid="checkout-ship-zip" /></F>
              <F label="Città" required testId="f-city"><Input value={shipping.city} onChange={(e) => setShipping({ ...shipping, city: e.target.value })} className="rounded-none" data-testid="checkout-ship-city" /></F>
              <F label="Provincia" required testId="f-province"><Input maxLength={2} value={shipping.province} onChange={(e) => setShipping({ ...shipping, province: e.target.value.toUpperCase() })} className="rounded-none" data-testid="checkout-ship-province" /></F>
              <F label="Nazione" required testId="f-country"><Input value={shipping.country} onChange={(e) => setShipping({ ...shipping, country: e.target.value })} className="rounded-none" data-testid="checkout-ship-country" /></F>
              <div className="sm:col-span-2"><F label="Informazioni aggiuntive (scala, interno, citofono…)" testId="f-extra"><Input value={shipping.extra} onChange={(e) => setShipping({ ...shipping, extra: e.target.value })} className="rounded-none" data-testid="checkout-ship-extra" /></F></div>
              <div className="sm:col-span-2"><F label="Note per la consegna" testId="f-notes"><Textarea rows={2} value={shipping.notes} onChange={(e) => setShipping({ ...shipping, notes: e.target.value })} className="rounded-none" data-testid="checkout-ship-notes" /></F></div>
            </div>
          </section>

          {/* Fatturazione */}
          <section className="bg-white border border-hair p-6 md:p-8" data-testid="checkout-billing">
            <div className="eyebrow mb-5">3 · Dati di fatturazione</div>
            <div className="flex flex-wrap gap-2 mb-5" data-testid="checkout-billing-type">
              {[["privato", "Privato"], ["azienda", "Azienda"], ["professionista", "Professionista"]].map(([v, l]) => (
                <button key={v} type="button" onClick={() => setBillingType(v)} data-testid={`billing-type-${v}`}
                  className={`px-4 py-2.5 min-h-[44px] text-xs tracking-[0.12em] uppercase border transition-colors ${billingType === v ? "bg-ink text-bone border-ink" : "border-hair hover:border-ink"}`}>{l}</button>
              ))}
            </div>
            <label className="flex items-center gap-3 text-sm cursor-pointer min-h-[44px]">
              <input type="checkbox" checked={billingSame} onChange={(e) => setBillingSame(e.target.checked)} className="w-5 h-5 accent-[#9E8462]" data-testid="checkout-billing-same" />
              L'indirizzo di fatturazione coincide con quello di spedizione.
            </label>
            {!billingSame && (
              <div className="grid sm:grid-cols-2 gap-4 mt-5">
                <div className="sm:col-span-2"><F label={billingType === "privato" ? "Nome e cognome" : "Ragione sociale"} required testId="f-billname"><Input value={billing.name} onChange={(e) => setBilling({ ...billing, name: e.target.value })} className="rounded-none" data-testid="checkout-bill-name" /></F></div>
                <F label="Indirizzo" required testId="f-billstreet"><Input value={billing.street} onChange={(e) => setBilling({ ...billing, street: e.target.value })} className="rounded-none" data-testid="checkout-bill-street" /></F>
                <F label="CAP" required testId="f-billzip"><Input inputMode="numeric" maxLength={5} value={billing.zip} onChange={(e) => setBilling({ ...billing, zip: e.target.value.replace(/\D/g, "") })} className="rounded-none" data-testid="checkout-bill-zip" /></F>
                <F label="Città" required testId="f-billcity"><Input value={billing.city} onChange={(e) => setBilling({ ...billing, city: e.target.value })} className="rounded-none" data-testid="checkout-bill-city" /></F>
                <F label="Provincia" required testId="f-billprovince"><Input maxLength={2} value={billing.province} onChange={(e) => setBilling({ ...billing, province: e.target.value.toUpperCase() })} className="rounded-none" data-testid="checkout-bill-province" /></F>
                <F label="Nazione" testId="f-billcountry"><Input value={billing.country} onChange={(e) => setBilling({ ...billing, country: e.target.value })} className="rounded-none" data-testid="checkout-bill-country" /></F>
              </div>
            )}
            <div className="grid sm:grid-cols-2 gap-4 mt-5">
              <F label="Codice fiscale" required={billingType === "privato" ? false : false} testId="f-cf"><Input value={fiscal.fiscal_code} onChange={(e) => setFiscal({ ...fiscal, fiscal_code: e.target.value.toUpperCase() })} className="rounded-none" data-testid="checkout-fiscal-code" /></F>
              {billingType !== "privato" && (
                <>
                  <F label="Partita IVA" required testId="f-vat"><Input value={fiscal.vat_number} onChange={(e) => setFiscal({ ...fiscal, vat_number: e.target.value })} className="rounded-none" data-testid="checkout-vat" /></F>
                  <F label="Codice destinatario (SDI)" testId="f-sdi"><Input maxLength={7} value={fiscal.sdi_code} onChange={(e) => setFiscal({ ...fiscal, sdi_code: e.target.value.toUpperCase() })} className="rounded-none" data-testid="checkout-sdi" /></F>
                  <F label="PEC" testId="f-pec"><Input type="email" value={fiscal.pec} onChange={(e) => setFiscal({ ...fiscal, pec: e.target.value })} className="rounded-none" data-testid="checkout-pec" /></F>
                </>
              )}
            </div>
          </section>
        </div>

        {/* Riepilogo */}
        <div className="lg:col-span-5 lg:sticky lg:top-6">
          <section className="bg-white border border-hair p-6 md:p-8" data-testid="checkout-summary">
            <div className="eyebrow mb-5">Riepilogo ordine</div>
            <div className="space-y-4">
              {cart.items.map((i) => (
                <div key={i.key || i.product_id} className="flex gap-3 text-sm" data-testid={`summary-item-${i.product_id}`}>
                  <img src={resolveMedia(i.image)} alt="" className="w-14 h-14 object-cover shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="truncate">{i.product_name}</div>
                    {i.variant && <div className="text-xs text-taupe">{i.variant}</div>}
                    {i.lead_time && <div className="text-[10px] text-amber-700 uppercase tracking-wide">Tempistica: {i.lead_time}</div>}
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-xs text-taupe">{i.quantity} × {eur(i.price)}</div>
                    <div>{eur(i.price * i.quantity)}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-5">
              <Input value={coupon} onChange={(e) => setCoupon(e.target.value)} placeholder="Codice sconto (se disponibile)" className="rounded-none" data-testid="checkout-coupon" />
            </div>
            <dl className="mt-5 space-y-2 text-sm border-t border-hair pt-4">
              <div className="flex justify-between"><dt className="text-taupe">Subtotale</dt><dd data-testid="summary-subtotal">{eur(cart.total)}</dd></div>
              {studioSavings > 0 && (
                <div className="flex justify-between text-gold"><dt>Vantaggio Clienti Studio (−{studioPercent}%)</dt><dd data-testid="summary-studio-discount">−{eur(studioSavings)}</dd></div>
              )}
              <div className="flex justify-between"><dt className="text-taupe">Spedizione</dt><dd data-testid="summary-shipping">{shippingCost === 0 ? "Gratuita" : eur(shippingCost)}</dd></div>
              <div className="flex justify-between text-[11px] text-taupe"><dt>Imposte</dt><dd>Incluse ove applicabili</dd></div>
              {gcApplied > 0 && (
                <div className="flex justify-between text-gold"><dt>Gift Card {gcCode.trim().toUpperCase()}</dt><dd data-testid="summary-giftcard">−{eur(gcApplied)}</dd></div>
              )}
              <div className="flex justify-between items-baseline border-t border-hair pt-3"><dt className="eyebrow">Totale da pagare</dt><dd className="font-serif text-3xl" data-testid="summary-total">{eur(finalTotal)}</dd></div>
            </dl>
            {studioActive && (
              <p className="text-[11px] text-gold mt-2" data-testid="summary-studio-note">Stai acquistando con le condizioni riservate ai clienti dello Studio (−{studioPercent}% sui prodotti idonei), applicate automaticamente.</p>
            )}
            {leadTimes.length > 0 && (
              <p className="text-[11px] text-taupe mt-3" data-testid="summary-leadtimes">Tempi indicativi: {leadTimes.join(" · ")} — decorrono dalla conferma dell'ordine.</p>
            )}
            <p className="text-[11px] text-taupe mt-1">Spedizione a: {shipping.city ? `${shipping.city} (${shipping.province || "—"})` : "da compilare"} · Fatturazione: {billingType}{billingSame ? ", come spedizione" : ", indirizzo dedicato"}</p>

            <div className="mt-6 space-y-3" data-testid="checkout-consents">
              <label className="flex items-start gap-3 text-xs text-taupe leading-relaxed cursor-pointer min-h-[44px]">
                <input type="checkbox" checked={accept.terms} onChange={(e) => setAccept({ ...accept, terms: e.target.checked })} className="w-5 h-5 mt-0.5 shrink-0 accent-[#9E8462]" data-testid="accept-terms" />
                <span>Accetto le <Link to="/termini" target="_blank" className="underline hover:text-ink">condizioni di vendita</Link>. *</span>
              </label>
              <label className="flex items-start gap-3 text-xs text-taupe leading-relaxed cursor-pointer min-h-[44px]">
                <input type="checkbox" checked={accept.privacy} onChange={(e) => setAccept({ ...accept, privacy: e.target.checked })} className="w-5 h-5 mt-0.5 shrink-0 accent-[#9E8462]" data-testid="accept-privacy" />
                <span>Ho letto l'<Link to="/privacy" target="_blank" className="underline hover:text-ink">informativa privacy</Link>. *</span>
              </label>
              <label className="flex items-start gap-3 text-xs text-taupe leading-relaxed cursor-pointer min-h-[44px]">
                <input type="checkbox" checked={accept.returns} onChange={(e) => setAccept({ ...accept, returns: e.target.checked })} className="w-5 h-5 mt-0.5 shrink-0 accent-[#9E8462]" data-testid="accept-returns" />
                <span>Accetto la politica di reso: {settings.shop_returns_info} *</span>
              </label>
              <label className="flex items-start gap-3 text-xs text-taupe leading-relaxed cursor-pointer min-h-[44px]">
                <input type="checkbox" checked={accept.marketing} onChange={(e) => setAccept({ ...accept, marketing: e.target.checked })} className="w-5 h-5 mt-0.5 shrink-0 accent-[#9E8462]" data-testid="accept-marketing" />
                <span>Desidero ricevere aggiornamenti, novità e comunicazioni commerciali da Giovanna Matino Interior. (facoltativo)</span>
              </label>
            </div>

            <div className="mt-6 border-t border-hair pt-5" data-testid="checkout-giftcard">
              <div className="text-[10px] tracking-[0.14em] uppercase text-taupe mb-2">Hai una Gift Card?</div>
              <div className="flex gap-2">
                <Input value={gcCode} onChange={(e) => { setGcCode(e.target.value.toUpperCase()); setGc(null); }} placeholder="GMI-XXXX-XXXX-XXXX" className="rounded-none font-mono text-base" data-testid="giftcard-code-input" />
                <button type="button" onClick={validateGc} disabled={gcBusy || !gcCode.trim()} className="min-h-[44px] px-4 border border-ink text-xs tracking-[0.12em] uppercase hover:bg-ink hover:text-bone transition-colors disabled:opacity-40 shrink-0" data-testid="giftcard-apply">
                  {gcBusy ? "…" : "Applica"}
                </button>
              </div>
              {gc && <p className="text-[11px] text-gold mt-2" data-testid="giftcard-applied-note">Credito applicato: −{eur(gcApplied)} · saldo residuo dopo questo ordine: {eur(Math.max(0, gc.balance - gcApplied))}</p>}
            </div>

            <div className="mt-6">
              <div className="text-[10px] tracking-[0.14em] uppercase text-taupe mb-3">Metodo di pagamento</div>
              <div className="grid grid-cols-2 gap-2" data-testid="checkout-methods">
                <button type="button" onClick={() => setMethod("stripe")} data-testid="method-stripe"
                  className={`px-4 py-3 min-h-[48px] text-xs tracking-[0.12em] uppercase border transition-colors ${method === "stripe" ? "bg-ink text-bone border-ink" : "border-hair hover:border-ink"}`}>
                  Carta · Stripe
                </button>
                <button type="button" onClick={() => setMethod("paypal")} disabled={!paypalCfg?.enabled} data-testid="method-paypal"
                  className={`px-4 py-3 min-h-[48px] text-xs tracking-[0.12em] uppercase border transition-colors disabled:opacity-40 ${method === "paypal" ? "bg-ink text-bone border-ink" : "border-hair hover:border-ink"}`}>
                  PayPal
                </button>
              </div>

              {method === "stripe" ? (
                <button onClick={payStripe} disabled={busy || !valid} className="btn-ink w-full min-h-[52px] mt-4 disabled:opacity-40 inline-flex items-center justify-center gap-2" data-testid="checkout-pay">
                  {busy ? <Loader2 size={16} className="animate-spin" /> : <CreditCard size={16} />}
                  {busy ? "Reindirizzamento…" : finalTotal <= 0 ? "Conferma ordine (coperto dalla Gift Card)" : `Paga ${eur(finalTotal)}`}
                </button>
              ) : (
                <div className="mt-4" data-testid="checkout-paypal-buttons">
                  {!valid && <p className="text-xs text-amber-700 mb-2">Completa i dati obbligatori e le accettazioni per attivare PayPal.</p>}
                  {valid && paypalCfg?.client_id && (
                    <PayPalScriptProvider options={{ clientId: paypalCfg.client_id, currency: "EUR", locale: "it_IT", intent: "capture" }}>
                      <PayPalButtons
                        style={{ layout: "vertical", shape: "rect", label: "pay" }}
                        forceReRender={[cart.total, shippingCost]}
                        createOrder={startPaypal}
                        onApprove={async (data) => {
                          try {
                            await api.post(`/payments/paypal/capture/${data.orderID}`);
                            cart.clear();
                            navigate(`/ordine/conferma?session_id=${data.orderID}`);
                          } catch (err) {
                            toast.error(err.response?.data?.detail || "Verifica pagamento non riuscita");
                          }
                        }}
                        onError={() => toast.error("PayPal non disponibile. Riprova o paga con carta.")}
                      />
                    </PayPalScriptProvider>
                  )}
                </div>
              )}
              <p className="text-[11px] text-taupe mt-4 text-center">Pagamento sicuro. L'ordine è confermato solo dopo la verifica del pagamento. La fattura verrà inviata successivamente via email.</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
